/**
 * @description Component-AmountAdjustor
 */
import './index.less';

import { FC, useCallback, useEffect, useMemo } from 'react';

import { inputNumChecker, toBN } from '@/utils/numberUtil';

import { DEFAULT_DECIMALS } from '@/constants/global';
import { LIMIT_ORDER_MARK_PRICE_SLIPPAGE, TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useMainPosition } from '@/features/account/positionHook';
import {
  useLimitFormState,
  useSimulationBalanceChange,
  useTradeSide,
  useTradeStoreTransformed,
  useTradeType,
} from '@/features/trade/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import SymbolSelector from '@/pages/trade/CoinPriceChart/SymbolSelector';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Input from '../Input';
import SynSquareSlider from '../SynSquareSlider';

interface AmountAdjustorProps {
  disabled?: boolean;
  currentPair?: WrappedPair | undefined;
  onBaseChange?: (value: string) => void;
  onQuoteChange?: (value: string) => void;
}

const marks = {
  0: '0%',
  25: '25%',
  50: '50%',
  75: '75%',
  100: '100%',
};

const AmountAdjustor: FC<AmountAdjustorProps> = function ({
  disabled = false,
  currentPair,
  onBaseChange,
  onQuoteChange,
}) {
  const { t } = useTranslation();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const tradeType = useTradeType(chainId);
  const {
    setTradeSize,
    setTradeQuoteSize,
    leverage,
    setTradePercent,
    tradePercent,
    tradeSize,
    isUnitInBaseToken,
    setIsUnitInBaseToken,
    tradeQuoteSize,
  } = useTradeStoreTransformed();
  const limitFormState = useLimitFormState(chainId);
  const tradeSide = useTradeSide(chainId);
  const currentPosition = useMainPosition(chainId, userAddr, currentPair?.id);
  const { totalBalanceBefore } = useSimulationBalanceChange(chainId, currentPair?.rootInstrument?.quoteToken);
  const maxSize = useMemo(() => {
    let calculatedSize =
      totalBalanceBefore && leverage && (!!currentPair?.fairPrice || !!toBN(limitFormState.limitPrice || 0).eq(toBN(0)))
        ? totalBalanceBefore
            .decimalValue(currentPair?.rootInstrument?.marginToken?.decimals || DEFAULT_DECIMALS)
            .mul(leverage)
            .div(
              isUnitInBaseToken
                ? tradeType === TRADE_TYPE.MARKET
                  ? currentPair?.wrapAttribute('fairPrice') || 1
                  : (currentPair
                      ?.wrapAttribute('markPrice')
                      .mul(LIMIT_ORDER_MARK_PRICE_SLIPPAGE)
                      .gt(limitFormState.limitPrice || 0)
                      ? currentPair?.wrapAttribute('markPrice').mul(LIMIT_ORDER_MARK_PRICE_SLIPPAGE)
                      : limitFormState.limitPrice) || 1
                : 1,
            )
        : totalBalanceBefore;
    if (tradeType === TRADE_TYPE.MARKET && tradeSide !== currentPosition?.side) {
      if (isUnitInBaseToken) {
        calculatedSize = calculatedSize?.add(currentPosition?.wrapAttribute('size').abs() || 0);
      } else {
        calculatedSize = calculatedSize?.add(
          currentPosition
            ?.wrapAttribute('size')
            .abs()
            .mul(currentPair?.wrapAttribute('fairPrice') || 1) || 0,
        );
      }
    }

    return calculatedSize;
  }, [
    totalBalanceBefore,
    leverage,
    currentPair,
    limitFormState.limitPrice,
    isUnitInBaseToken,
    tradeType,
    tradeSide,
    currentPosition,
  ]);
  const value = useMemo(
    () => (isUnitInBaseToken ? tradeSize : tradeQuoteSize),
    [tradeSize, tradeQuoteSize, isUnitInBaseToken],
  );
  const decimals = isUnitInBaseToken
    ? currentPair?.rootInstrument.baseToken.decimals
    : currentPair?.rootInstrument.quoteToken.decimals;
  const onInputChanage = useCallback(
    (value: string) => {
      const checkedSize = inputNumChecker(value, decimals).trim();
      // let finalValue = checkedValue;
      // Ensure checkedValue doesn't exceed maxSize
      // if (maxSize && checkedValue) {
      //   const checkedBN = WrappedBigNumber.from(checkedValue);
      //   if (checkedBN.gt(maxSize)) {
      //     finalValue = maxSize.toString();
      //   }
      // }

      if (isUnitInBaseToken) {
        setTradeSize(checkedSize);
        setTradeQuoteSize(
          inputNumChecker(
            WrappedBigNumber.from(checkedSize || 0).mul(currentPair?.wrapAttribute('fairPrice') || 1).stringValue,
            currentPair?.rootInstrument.quoteToken.decimals,
          ),
        );
      } else {
        setTradeQuoteSize(checkedSize);
        setTradeSize(
          inputNumChecker(
            WrappedBigNumber.from(checkedSize || 0).div(currentPair?.wrapAttribute('fairPrice') || 1).stringValue,
            currentPair?.rootInstrument.baseToken.decimals,
          ),
        );
      }

      if (onBaseChange && onQuoteChange) {
        if (isUnitInBaseToken) {
          onBaseChange(checkedSize);
        } else {
          onQuoteChange(checkedSize);
        }
      }

      const tradePercent = maxSize
        ? Number(
            WrappedBigNumber.from(checkedSize || 0)
              .div(maxSize)
              .mul(100)
              .toNumber()
              .toFixed(2),
          )
        : 100;
      setTradePercent(tradePercent);
    },
    [
      decimals,
      isUnitInBaseToken,
      onBaseChange,
      onQuoteChange,
      maxSize,
      setTradePercent,
      setTradeSize,
      setTradeQuoteSize,
      currentPair,
    ],
  );
  const onSliderChange = useCallback(
    (value: number) => {
      setTradePercent(value);
      if (value === 0) {
        setTradeSize('');
        setTradeQuoteSize('');
        return;
      }
      const size = maxSize ? maxSize.mul(value).div(100).toString() : '';
      const checkedSize = inputNumChecker(size, decimals);
      if (isUnitInBaseToken) {
        setTradeSize(checkedSize);
        setTradeQuoteSize(
          inputNumChecker(
            WrappedBigNumber.from(checkedSize || 0).mul(currentPair?.wrapAttribute('fairPrice') || 1).stringValue,
            currentPair?.rootInstrument.quoteToken.decimals,
          ),
        );
      } else {
        setTradeQuoteSize(checkedSize);
        setTradeSize(
          inputNumChecker(
            WrappedBigNumber.from(checkedSize || 0).div(currentPair?.wrapAttribute('fairPrice') || 1).stringValue,
            currentPair?.rootInstrument.baseToken.decimals,
          ),
        );
      }
      if (onBaseChange && onQuoteChange) {
        if (isUnitInBaseToken) {
          onBaseChange(checkedSize);
        } else {
          onQuoteChange(checkedSize);
        }
      }
    },
    [
      currentPair,
      decimals,
      isUnitInBaseToken,
      maxSize,
      onBaseChange,
      onQuoteChange,
      setTradePercent,
      setTradeQuoteSize,
      setTradeSize,
    ],
  );
  useEffect(() => {
    if (tradeType === TRADE_TYPE.LIMIT) {
      onInputChanage(tradeSize);
    }
    //  update size when limitprice change
  }, [limitFormState.limitPrice]);

  useEffect(() => {
    if (currentPair?.id) {
      setTradeSize('');
      onSliderChange(0);
    }
  }, [currentPair?.id]);

  useEffect(() => {
    //  must use this to update tradeSize and tradeQuoteSize
    onSliderChange(tradePercent);
    //update when these parameters change
  }, [isUnitInBaseToken]);
  // useEffect(() => {
  //   if (tradeType === TRADE_TYPE.MARKET) {
  //     onSliderChange(tradePercent);
  //   }
  //   //update when these parameters change
  // }, [tradeSide, currentPair?.fairPrice._hex]);

  return (
    <div className="syn-amount-adjustor">
      <div className="syn-amount-adjustor-title">
        {tradeType === TRADE_TYPE.SCALE_LIMIT ? t('common.totalSize') : t('common.size')}
      </div>
      <div className={classNames('syn-amount-adjustor-input', disabled && 'disabled')}>
        <Input
          {...{
            type: 'text',
            // pattern: '^[0-9]*[.,]?[0-9]*$',
            inputMode: 'decimal',
            autoComplete: 'off',
            autoCorrect: 'off',
            step: 1e18,
            onKeyDown: (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
          }}
          disabled={disabled}
          placeholder="0.0"
          // tokenInfo={isUnitInBaseToken ? currentPair?.rootInstrument.baseToken : currentPair?.rootInstrument.quoteToken}
          value={value}
          onChange={(e) => onInputChanage(e.target.value)}
        />
        <SymbolSelector
          isBase={isUnitInBaseToken}
          onChange={(isBase) => {
            setIsUnitInBaseToken(isBase);
          }}
          baseToken={currentPair?.rootInstrument.baseToken}
          quoteToken={currentPair?.rootInstrument.quoteToken}
        />
      </div>
      <div className="syn-amount-adjustor-bottom">
        <div className="syn-amount-adjustor-slider">
          <SynSquareSlider
            value={tradePercent}
            onChange={onSliderChange}
            min={0}
            max={100}
            step={1}
            ticks={5}
            marks={marks}
            disabled={disabled}
            formatSuffix="%"
            tooltip={{
              formatter: (number) => <>{number}%</>,
            }}
          />
        </div>

        <Input
          value={tradePercent}
          disabled={disabled}
          {...{
            type: 'text',
            // pattern: '^[0-9]*$',
            inputMode: 'numeric',
            autoComplete: 'off',
            autoCorrect: 'off',
            onKeyDown: (e) => ['e', 'E', '+', '-', '.', ','].includes(e.key) && e.preventDefault(),
            step: 1,
          }}
          onChange={(e) => {
            const value = e.target.value;
            const numValue = parseInt(value, 10);
            if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 100)) {
              onSliderChange(numValue || 0);
            }
          }}
          suffix={'%'}
        />
      </div>
    </div>
  );
};

export default AmountAdjustor;
